import React, { Suspense, useEffect, useState } from "react";
import { Router, Route, Redirect, Switch, useLocation } from "react-router-dom";

import { history } from "../_config";
import { store } from "../_store";
import { connect, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes, withToast } from "../contexts/toastr.context";
import DashboardPage, { DASHBOARD_ROUTE } from "../_pages/dashboard.page";
import LoginPage from "../_pages/login.page";
import { LOGIN_ROUTE } from "../_pages/login.page";
import { Loader } from "./loader.component";
import UserManagementPage, {
  USER_MANAGEMENT_ROUTE,
} from "../_pages/user-management.page";
import OrgManagementPage, {
  ORG_MANAGEMENT_ROUTE,
} from "../_pages/org-management.page";
import PublicRoute from "./public-route.component";
import PrivateRoute from "./private-route.component";
import Error404 from "../_pages/not-found.page";
import Sidebar from "./sidebar.component";
import OrgDetailsPage, {
  ORGANIZATION_ROUTE,
  ORG_DETAILS_ROUTE,
} from "../_pages/org-details.page";
import ResetPasswordPage, {
  RESET_PASSWORD_ROUTE,
} from "../_pages/reset-password.page";
import CreateOrgPage, {
  CREATE_ORGANIZATION_ROUTE,
  EDIT_ORGANIZATION_ROUTE,
  UPDATE_ORGANIZATION_ROUTE,
} from "../_pages/create-org.page";
import ForgotPasswordPage, {
  FORGOT_PASSWORD_ROUTE,
} from "../_pages/forgot-password.page";
import AnnouncementPage, {
  ANNOUNCEMENT_ROUTE,
} from "../_pages/announcement.page";
import TrackManagementPage, {
  TRACK_MANAGEMENT_ROUTE,
} from "../_pages/track-management.page";
import { UserService } from "../_service/user.service";
import { createSession } from "../_store/_actions/auth.actions";
import SurveyPage, { SURVEY_ROUTE } from "../_pages/survey.page";
import CreateSurveyPage, {
  CREATE_SURVEY_ROUTE,
} from "../_pages/create-survey-page";
import SurveyDetailsPage, {
  SURVEY_DETAILS_ROUTE,
} from "../_pages/survey-details.page";
import CommunityManagementPage, {
  COMMUNITY_MANAGEMENT_ROUTE,
} from "../_pages/community-management-page";
import OrgCommunityPage, {
  ORGANIZATION_COMMUNITY_ROUTE,
} from "../_pages/organization-community-page";
import ManageCommunityPage, {
  MANAGE_COMMUNITY_ROUTE,
} from "../_pages/manage-community-group";
import ManageGroupPage, {
  MANAGE_GROUP_ROUTE,
} from "../_pages/manage-group.page";
import EngagementAnalyticsPage, {
  ENGAGEMENT_ANALYTICS_ROUTE,
} from "../_pages/engagement-analytics.page";
import MixPanelPage, { MIXPANEL_ROUTE } from "../_pages/mixpanel.page";

// lazy loaded components
const Header = React.lazy(() => import("./header.component"));
// const Sidebar = React.lazy(() => import("./sidebar.component"));
// lazy loaded pages
// const GroupPage = React.lazy(() => import("../_pages/group.page"));

function AuthAppComponent({ user, addToast, setUser }) {
  let dispatch = useDispatch();

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const token = JSON.parse(localStorage.getItem("token"));
      if (token) {
        UserService.getUserProfile(token)
          .then((response) => {
            // save the user
            localStorage.setItem(
              "user",
              JSON.stringify({
                token: token,
                email: response.email ? response.email : response.email,
                name: response.name,
                role: response.roles[0] ? response.roles[0] : null,
              })
            );

            dispatch(
              createSession({
                token: token,
                email: response.email ? response.email : response.email,
                name: response.name,
                role: response.roles[0] ? response.roles[0] : null,
              })
            );
          })
          .catch((error) => {
            // check for token expiry
            if (
              error &&
              error.response &&
              (error.response.data.message ===
                "Cannot read property 'user' of null" ||
                error.response.data.message === "JWT Token is required" ||
                error.response.data.message === "Authentication error!" ||
                error.response.data.message === "jwt expired")
            ) {
              // addToast(error.response.data.message, "", ToastTypes.danger);
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              setUser(null);
              setTimeout(() => {
                history.push("/login");
              }, 1500);
            }
          });
      }
    });
    return function cleanup() {
      unlisten();
    };
  }, []);
  return (
    <div className="AuthApp">
      <Router history={history}>
        <Switch>
          <>
            <Suspense>
              <Header user={user} />
            </Suspense>
            <PublicRoute component={LoginPage} user={user} path={LOGIN_ROUTE} />
            <PublicRoute
              component={ResetPasswordPage}
              user={user}
              path={RESET_PASSWORD_ROUTE}
            />
            <PublicRoute
              component={ForgotPasswordPage}
              user={user}
              path={FORGOT_PASSWORD_ROUTE}
            />
            <div className="AppContainer">
              <Suspense fallback={<Loader />}>
                {user && user.token ? <Sidebar /> : <></>}
              </Suspense>
              <PrivateRoute
                component={DashboardPage}
                user={user}
                path={DASHBOARD_ROUTE}
                exact
              />
              <PrivateRoute
                component={UserManagementPage}
                user={user}
                path={USER_MANAGEMENT_ROUTE}
                exact
              />
              <PrivateRoute
                component={OrgManagementPage}
                user={user}
                roleFor="superAdmin"
                path={ORG_MANAGEMENT_ROUTE}
                exact
              />
              <PrivateRoute
                component={OrgDetailsPage}
                user={user}
                path={ORGANIZATION_ROUTE}
                exact
              />
              <PrivateRoute
                component={CreateOrgPage}
                user={user}
                roleFor="superAdmin"
                path={CREATE_ORGANIZATION_ROUTE}
                exact
              />
              <PrivateRoute
                component={AnnouncementPage}
                user={user}
                path={ANNOUNCEMENT_ROUTE}
                exact
              />
              <PrivateRoute
                component={TrackManagementPage}
                user={user}
                roleFor="superAdmin"
                path={TRACK_MANAGEMENT_ROUTE}
                exact
              />
              <PrivateRoute
                component={SurveyPage}
                user={user}
                roleFor={
                  user &&
                  (user.role === "orgAdmin" || user.role === "superAdmin")
                    ? user.role
                    : null
                }
                path={SURVEY_ROUTE}
                exact
              />
              <PrivateRoute
                component={CreateSurveyPage}
                user={user}
                roleFor={
                  user &&
                  (user.role === "orgAdmin" || user.role === "superAdmin")
                    ? user.role
                    : null
                }
                path={CREATE_SURVEY_ROUTE}
                exact
              />
              <PrivateRoute
                component={SurveyDetailsPage}
                user={user}
                roleFor={
                  user &&
                  (user.role === "orgAdmin" || user.role === "superAdmin")
                    ? user.role
                    : null
                }
                path={SURVEY_DETAILS_ROUTE}
                exact
              />
              <PrivateRoute
                component={CommunityManagementPage}
                user={user}
                roleFor="superAdmin"
                path={COMMUNITY_MANAGEMENT_ROUTE}
                exact
              />
              <PrivateRoute
                component={OrgCommunityPage}
                user={user}
                roleFor="superAdmin"
                path={ORGANIZATION_COMMUNITY_ROUTE}
                exact
              />
              <PrivateRoute
                component={ManageCommunityPage}
                user={user}
                roleFor="superAdmin"
                path={MANAGE_COMMUNITY_ROUTE}
                exact
              />
              <PrivateRoute
                component={ManageGroupPage}
                user={user}
                roleFor="superAdmin"
                path={MANAGE_GROUP_ROUTE}
                exact
              />
              <PrivateRoute
                component={EngagementAnalyticsPage}
                user={user}
                roleFor="orgAdmin"
                path={ENGAGEMENT_ANALYTICS_ROUTE}
                exact
              />
              {/* <PrivateRoute
                component={MixPanelPage}
                user={user}
                roleFor="orgAdmin"
                path={MIXPANEL_ROUTE}
                exact
              /> */}
            </div>
            {/* <Route path="*" exact={true} component={Error404} /> */}
          </>
        </Switch>
      </Router>
    </div>
  );
}

// const AuthApp = AuthAppComponent;
const AuthApp = withToast(
  connect(
    (s) => ({}),
    (d) => bindActionCreators({ setUser: createSession }, d)
  )(AuthAppComponent)
);

export default AuthApp;
